import type {
  ApiResourceDescriptor,
  ApiResourceRoute,
} from "../resources/types";
import type { ClientType } from "../routing/clientType";
import {
  generateRoute,
  generateRoutes,
  unwrapRoute,
  unwrapRoutes,
} from "../routing/resolveRoute";
import type { ApiBatchRoute, ApiRoute } from "../routing/types";
import type {
  ApiBatchMode,
  ApiCacheResource,
  ApiKey,
  ApiKeyNamespace,
} from "./types";

type ApiKeyOptions = Readonly<{
  batchMode?: ApiBatchMode;
  cacheResource?: ApiCacheResource;
}>;

export function createResourceApiKey<TEntity>(
  resourceRoute: ApiResourceRoute<TEntity> | null | undefined,
  namespace: ApiKeyNamespace,
): ApiKey | null {
  if (resourceRoute == null) return null;
  const { route, descriptor, collectionRoute } = resourceRoute;
  return createApiKey(route, namespace, descriptor.clientType, {
    cacheResource: createApiCacheResource(descriptor, collectionRoute),
  });
}

export function createApiCacheResource(
  descriptor: ApiResourceDescriptor | undefined,
  collectionRoute?: string,
): ApiCacheResource | undefined {
  if (descriptor == null) return;
  if (collectionRoute == null) return { tag: descriptor.cacheTag };

  return {
    tag: descriptor.cacheTag,
    collectionRoute,
  };
}

function addApiKeyOptions(key: ApiKey, options: ApiKeyOptions): ApiKey {
  let keyWithOptions = key;

  if (options.cacheResource) {
    keyWithOptions = {
      ...keyWithOptions,
      cacheResource: options.cacheResource,
    };
  }

  if (options.batchMode) {
    keyWithOptions = {
      ...keyWithOptions,
      batchMode: options.batchMode,
    };
  }

  return keyWithOptions;
}

export function createApiKey(
  route: ApiRoute | ApiBatchRoute,
  namespace: ApiKeyNamespace,
  clientType: ClientType,
  options: ApiKeyOptions = {},
): ApiKey | null {
  if (Array.isArray(route)) {
    const routes = unwrapRoutes(route);
    const requestedRoutes = generateRoutes(routes, clientType).filter(
      (requestedRoute) => !!requestedRoute,
    );
    if (!routes.length || requestedRoutes.length !== routes.length) return null;

    return addApiKeyOptions(
      {
        namespace,
        clientType,
        route: routes,
        requestedRoute: requestedRoutes,
      },
      options,
    );
  }

  const unwrappedRoute = unwrapRoute(route as ApiRoute);
  // An explicit empty route addresses the tenant/portfolio itself. Null,
  // undefined and whitespace-only routes still disable the request.
  if (!unwrappedRoute && route !== "") return null;
  const requestedRoute = generateRoute(unwrappedRoute, clientType);
  if (!requestedRoute) return null;

  return addApiKeyOptions(
    {
      namespace,
      clientType,
      route: unwrappedRoute,
      requestedRoute,
    },
    options,
  );
}

export function isApiGetKey(key: unknown): key is ApiKey {
  if (key == null || typeof key !== "object") return false;
  if (
    !("namespace" in key) ||
    !("clientType" in key) ||
    !("route" in key) ||
    !("requestedRoute" in key)
  ) {
    return false;
  }

  if (
    key.namespace !== "get" &&
    key.namespace !== "pagedGet" &&
    key.namespace !== "serverPagedGet"
  ) {
    return false;
  }

  function isRoute(route: unknown): route is string | readonly string[] {
    return (
      typeof route === "string" ||
      (Array.isArray(route) && route.every((item) => typeof item === "string"))
    );
  }

  return (
    typeof key.clientType === "string" &&
    isRoute(key.route) &&
    isRoute(key.requestedRoute)
  );
}

export function createApiBatchKey<TEntity>(
  resourceRoutes: readonly ApiResourceRoute<TEntity>[],
  batchMode: ApiBatchMode,
  namespace: "get" | "pagedGet" = "get",
): ApiKey | null {
  if (!resourceRoutes.length) return null;

  const { descriptor, collectionRoute } = resourceRoutes[0];
  for (const [index, resourceRoute] of resourceRoutes.entries()) {
    const memberDescriptor = resourceRoute.descriptor;
    if (
      memberDescriptor.cacheTag !== descriptor.cacheTag ||
      memberDescriptor.clientType !== descriptor.clientType ||
      resourceRoute.collectionRoute !== collectionRoute
    ) {
      throw new Error(
        `Batch GET member at index ${index} has a different cache scope. ` +
          `Expected: ${descriptor.cacheTag} / ${descriptor.clientType} / ${collectionRoute ?? "(no collection)"}. ` +
          `Received: ${memberDescriptor.cacheTag} / ${memberDescriptor.clientType} / ${resourceRoute.collectionRoute ?? "(no collection)"}.`,
      );
    }
  }

  const routes = resourceRoutes.map((resourceRoute) =>
    unwrapRoute(resourceRoute.route),
  );
  // Do not silently drop members: their positions correspond to result positions.
  if (routes.some((route) => !route)) return null;

  return createApiKey(routes, namespace, descriptor.clientType, {
    batchMode,
    cacheResource: createApiCacheResource(descriptor, collectionRoute),
  });
}
